// ABOUTME: Main entry point for the Vite React application
// ABOUTME: Heavy pages (charts, scalar, jspdf-using) are lazy-loaded via React.lazy + Suspense in AuthShell

// Global Tailwind + design-system styles. MUST be imported by the eager entry so
// the stylesheet ships in the head-linked entry bundle; importing it from a
// lazy-loaded route would leave every other route unstyled.
import './App.css';
import { configure as configureInference } from '@boundarylab/browser-inference';
import { BrowserInferenceProvider } from '@boundarylab/browser-inference/react';
import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthShell } from './components/AuthShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  DISCOVER_TABS,
  HubLayout,
  MARKET_TABS,
  RISK_TABS,
  STRATEGY_TABS,
  TRADE_TABS,
} from './components/hubs';
import { ModuleHostRoute } from './components/ModuleHostRoute';
import { inferenceDataSources } from './lib/inference/data-sources';
import { lazyWithReload } from './lib/lazy-with-reload';
import { bootstrapModules } from './lib/module-loader';
import { createQueryClient } from './lib/query-client';
import { routerFuture } from './lib/router-future';

// onnxruntime-web's wasm runtime is self-hosted on alpha-finder's R2 (custom
// domain modules.alpha.gpudump.com). See memory entry ort-wasm-r2-hosting for the
// upload procedure. Configured once at bootstrap; the BrowserInferenceProvider is
// mounted at the app root (Root, below) so the runtime initializes exactly once —
// lazy-mounting it under AuthShell's Suspense caused a double-init crash.
configureInference({ wasmPaths: 'https://modules.alpha.gpudump.com/ort-wasm/1.24.3/' });

// Public routes render outside AuthShell's <Suspense>, so they stay eager.
import PublishedSharePage from './pages/PublishedSharePage';

// Authenticated pages are lazy-loaded so their dependencies (recharts,
// lightweight-charts, onnx-consuming widgets, scalar, jspdf when triggered) stay
// out of the main bundle that loads before sign-in. They all render
// under AuthShell's <Suspense> boundary.
const App = lazyWithReload(() => import('./App'));
const ApiDocs = lazyWithReload(() => import('./pages/ApiDocs'));
const AssetRiskPage = lazyWithReload(() => import('./pages/AssetRiskPage'));
const BacktestingPage = lazyWithReload(() => import('./pages/BacktestingPage'));
const ConsumerModelsPage = lazyWithReload(() => import('./pages/ConsumerModelsPage'));
const CorrelationPage = lazyWithReload(() => import('./pages/CorrelationPage'));
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage'));
const DiscoveryGuidePage = lazyWithReload(() => import('./pages/DiscoveryGuidePage'));
const DiscoveryPage = lazyWithReload(() => import('./pages/DiscoveryPage'));
const EntryLeaderboardPage = lazyWithReload(() => import('./pages/EntryLeaderboardPage'));
const EntryPositionPage = lazyWithReload(() => import('./pages/EntryPositionPage'));
const FactorsPage = lazyWithReload(() => import('./pages/FactorsPage'));
const FundamentalsPage = lazyWithReload(() => import('./pages/FundamentalsPage'));
const GuidePage = lazyWithReload(() => import('./pages/GuidePage'));
const InferencePlaygroundPage = lazyWithReload(() => import('./pages/InferencePlaygroundPage'));
const MonitoringPage = lazyWithReload(() => import('./pages/MonitoringPage'));
const NotFoundPage = lazyWithReload(() => import('./pages/NotFoundPage'));
const OptionsPage = lazyWithReload(() => import('./pages/OptionsPage'));
const OtherPagesPage = lazyWithReload(() => import('./pages/OtherPagesPage'));
const PicksPage = lazyWithReload(() => import('./pages/PicksPage'));
const PlatformDiscoveryPage = lazyWithReload(() => import('./pages/PlatformDiscoveryPage'));
const PortfolioOptimizationPage = lazyWithReload(() => import('./pages/PortfolioOptimizationPage'));
const PortfolioPage = lazyWithReload(() => import('./pages/PortfolioPage'));
const PortfolioRiskPage = lazyWithReload(() => import('./pages/PortfolioRiskPage'));
const PortfoliosListPage = lazyWithReload(() => import('./pages/PortfoliosListPage'));
const PublishedAdminPage = lazyWithReload(() => import('./pages/PublishedAdminPage'));
const RealTimePage = lazyWithReload(() => import('./pages/RealTimePage'));
const RiskDecompositionPage = lazyWithReload(() => import('./pages/RiskDecompositionPage'));
const SentimentPage = lazyWithReload(() => import('./pages/SentimentPage'));
const SettingsPage = lazyWithReload(() => import('./pages/SettingsPage'));
const SymbolDetailPage = lazyWithReload(() => import('./pages/SymbolDetailPage'));
const ForecastPage = lazyWithReload(() => import('./pages/ForecastPage'));
const SymbolsPage = lazyWithReload(() => import('./pages/SymbolsPage'));
const TradeQueuePage = lazyWithReload(() => import('./pages/TradeQueuePage'));
const TutorialPage = lazyWithReload(() => import('./pages/TutorialPage'));
const ValidationPage = lazyWithReload(() => import('./pages/ValidationPage'));
const WatchlistPage = lazyWithReload(() => import('./pages/WatchlistPage'));
const WatchlistsListPage = lazyWithReload(() => import('./pages/WatchlistsListPage'));

const queryClient = createQueryClient();

// Preserves location.search on /watchlist → /watchlists hop so legacy
// bookmarks like /watchlist?add=AAPL keep their query string. A bare
// <Navigate to="/watchlists"/> would drop the search.
function LegacyWatchlistRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/watchlists', search: location.search }} replace />;
}

function Root() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserInferenceProvider dataSources={inferenceDataSources}>
          <BrowserRouter future={routerFuture}>
            <Routes>
              {/* Public routes — no auth, no sidebar/layout (no lazy children -> no Suspense needed) */}
              <Route path="/published/:shareToken" element={<PublishedSharePage />} />

              {/* Authenticated routes — share AuthGate + providers + Layout via AuthShell.
                AuthShell hosts the <Suspense> boundary so lazy page loads do not
                unmount providers and reset their state. */}
              <Route element={<AuthShell />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />

                {/* Discover hub — find-opportunities surfaces as tabs */}
                <Route element={<HubLayout tabs={DISCOVER_TABS} />}>
                  <Route path="/scanner" element={<App />} />
                  <Route path="/picks" element={<PicksPage />} />
                  <Route path="/entry-leaderboard" element={<EntryLeaderboardPage />} />
                  <Route path="/discovery" element={<DiscoveryPage />} />
                  <Route path="/discovery/guide" element={<DiscoveryGuidePage />} />
                  <Route path="/discovery/platform" element={<PlatformDiscoveryPage />} />
                </Route>

                {/* Trade hub */}
                <Route element={<HubLayout tabs={TRADE_TABS} />}>
                  <Route path="/trade-queue" element={<TradeQueuePage />} />
                  <Route path="/entry-positions" element={<EntryPositionPage />} />
                  <Route path="/entry-positions/:symbol" element={<EntryPositionPage />} />
                </Route>

                {/* Portfolios (standalone) */}
                <Route path="/portfolios" element={<PortfoliosListPage />} />
                <Route path="/portfolios/:id" element={<PortfolioPage />} />
                {/* Legacy alias kept for keyboard shortcut + old links */}
                <Route path="/portfolio" element={<Navigate to="/portfolios" replace />} />
                <Route path="/portfolio/:id" element={<PortfolioPage />} />

                {/* Watchlists (standalone) */}
                <Route path="/watchlists" element={<WatchlistsListPage />} />
                <Route path="/watchlists/:id" element={<WatchlistPage />} />
                {/* Legacy aliases — the wrapper preserves ?add= on the hop. */}
                <Route path="/watchlist" element={<LegacyWatchlistRedirect />} />
                <Route path="/watchlist/:id" element={<WatchlistPage />} />

                {/* Risk hub */}
                <Route path="/risk" element={<Navigate to="/risk/portfolio" replace />} />
                <Route element={<HubLayout tabs={RISK_TABS} />}>
                  <Route path="/risk/portfolio" element={<PortfolioRiskPage />} />
                  <Route path="/risk/asset" element={<AssetRiskPage />} />
                  <Route path="/risk-decomposition" element={<RiskDecompositionPage />} />
                  <Route path="/correlation" element={<CorrelationPage />} />
                  <Route path="/factors" element={<FactorsPage />} />
                </Route>

                {/* Strategy hub */}
                <Route element={<HubLayout tabs={STRATEGY_TABS} />}>
                  <Route path="/portfolio-optimization" element={<PortfolioOptimizationPage />} />
                  <Route path="/backtesting" element={<BacktestingPage />} />
                  <Route path="/options" element={<OptionsPage />} />
                </Route>

                {/* Market hub */}
                <Route element={<HubLayout tabs={MARKET_TABS} />}>
                  <Route path="/real-time" element={<RealTimePage />} />
                  <Route path="/fundamentals" element={<FundamentalsPage />} />
                  <Route path="/sentiment" element={<SentimentPage />} />
                </Route>

                {/* Symbols + Admin + System + Help — grouped in the menu, flat routes */}
                <Route path="/symbols" element={<SymbolsPage />} />
                <Route path="/symbol/:symbol" element={<SymbolDetailPage />} />
                <Route path="/forecast" element={<ForecastPage />} />
                <Route path="/forecast/:symbol" element={<ForecastPage />} />
                <Route path="/monitoring" element={<MonitoringPage />} />
                <Route path="/models" element={<ConsumerModelsPage />} />
                <Route path="/validation" element={<ValidationPage />} />
                <Route path="/inference" element={<InferencePlaygroundPage />} />
                <Route path="/admin/published" element={<PublishedAdminPage />} />
                <Route path="/guide" element={<GuidePage />} />
                <Route path="/tutorial" element={<TutorialPage />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="/other-pages" element={<OtherPagesPage />} />
                <Route path="/account" element={<Navigate to="/settings?tab=profile" replace />} />
                <Route path="/settings" element={<SettingsPage />} />
                {/* Runtime-loaded financial modules (Module Federation host). */}
                <Route path="/modules/:slug/*" element={<ModuleHostRoute />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </BrowserInferenceProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Kick off the module registry fetch + federation runtime init at app start.
// Failure is non-fatal — each <ModuleHost> surfaces its own per-slug load error.
void bootstrapModules();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);
